import { memo, useMemo } from "react";
import { 
  getRatingColor, 
  getRatingLabel, 
  normalizeRating,
  isValidRating,
} from "../../../utils/PathToPerformance/pathToPerformanceHelpers";
import { RATING_UNRATED, ARIA_LABELS } from "../../../constants/pathToPerformance";

/**
 * Props for the RatingTag component
 */
export interface RatingTagProps {
  /** Rating value (0, 1-5, or 99) */
  rating: number | string;
  /** Optional custom className */
  className?: string;
  /** Show rating label in aria-label for accessibility (default: true) */
  showAriaLabel?: boolean;
}

const RatingTag = memo<RatingTagProps>(({ 
  rating, 
  className = "",
  showAriaLabel = true,
}) => {
  // Memoize normalized rating to avoid recalculation
  const normalizedRating = useMemo(() => normalizeRating(rating), [rating]);
  
  // Validate rating value and use fallback for invalid ratings
  const validatedRating = useMemo(() => {
    if (!isValidRating(normalizedRating)) {
      console.warn(`Invalid rating value: ${rating}. Using ${RATING_UNRATED} as fallback.`);
      return RATING_UNRATED;
    }
    return normalizedRating;
  }, [normalizedRating, rating]);

  // Memoize color and label to avoid recalculation
  const bgColor = useMemo(() => getRatingColor(validatedRating), [validatedRating]);
  const ariaLabel = useMemo(() => {
    if (!showAriaLabel) return undefined;
    const label = getRatingLabel(validatedRating);
    return ARIA_LABELS.RATING(validatedRating, label);
  }, [validatedRating, showAriaLabel]);

  const displayRating = validatedRating.toString();

  return (
    <output
      className={`${bgColor} text-white flex items-center justify-center py-0.5 px-1 w-19.25 h-5 rounded text-center font-medium text-sm ${className}`}
      aria-label={ariaLabel}
    >
      {displayRating}
    </output>
  );
});

RatingTag.displayName = "RatingTag";

export default RatingTag;
